import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Shared mechanics for the anchored, searchable popovers (BadgeSelect,
 * StaffSelect, the Weekly Kanban badge picker): a trigger, a portaled panel that
 * flips up/down to fit the viewport, and dismissal on outside-tap / Escape.
 *
 * Mobile is the reason this is a hook rather than three copies. A touch keyboard
 * fires `resize` (Android shrinks the layout viewport) and `scroll` (iOS scrolls
 * the focused input into view) the instant the search box takes focus, so any
 * handler that closes on those two events closes the popover before it can be
 * used. Here they reposition instead, and we only close on a real viewport
 * change.
 */

const CLICK_THROUGH_MS = 250;

const finePointer = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: fine)").matches;

export default function useSearchPopover({ width, maxHeight, gap = 6 }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const anchorRef = useRef(null);
  const popRef = useRef(null);
  const openedAt = useRef(0);
  const openWidth = useRef(0);
  const raf = useRef(0);

  const isFine = finePointer();

  const compute = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    // Flip up only when there isn't room below and there's more room above.
    const openUp = spaceBelow < maxHeight && spaceAbove > spaceBelow;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    setCoords(
      openUp
        ? {
            left,
            bottom: window.innerHeight - r.top + gap,
            maxHeight: Math.min(maxHeight, spaceAbove - 12),
          }
        : {
            left,
            top: r.bottom + gap,
            maxHeight: Math.min(maxHeight, spaceBelow - 12),
          }
    );
  }, [width, maxHeight, gap]);

  const openMenu = useCallback(
    (e) => {
      e?.stopPropagation();
      compute();
      openedAt.current = Date.now();
      openWidth.current = window.innerWidth;
      setOpen(true);
    },
    [compute]
  );

  const close = useCallback(() => setOpen(false), []);

  /**
   * Wrap an option's select handler. The panel renders a few pixels below the
   * trigger — on touch that is underneath the finger that just opened it, and the
   * synthesized click a mobile browser dispatches after touchend is hit-tested
   * against whatever now sits at those coordinates. Without this, opening the
   * picker can silently apply the first option.
   */
  const guardSelect = useCallback(
    (fn) =>
      (...args) => {
        if (!isFine && Date.now() - openedAt.current < CLICK_THROUGH_MS) return;
        fn(...args);
      },
    [isFine]
  );

  useEffect(() => {
    if (!open) return undefined;

    const reposition = () => {
      cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(compute);
    };

    const onDocDown = (e) => {
      if (
        popRef.current?.contains(e.target) ||
        anchorRef.current?.contains(e.target)
      )
        return;
      setOpen(false);
    };

    const onKey = (e) => e.key === "Escape" && setOpen(false);

    // Follow the anchor rather than closing; scrolling inside the panel's own
    // list is not our business. Give up once the anchor leaves the viewport.
    const onScroll = (e) => {
      if (popRef.current?.contains(e.target)) return;
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r || r.bottom < 0 || r.top > window.innerHeight) {
        setOpen(false);
        return;
      }
      reposition();
    };

    // A soft keyboard only changes the height. A rotation or a genuine window
    // resize changes the width, and that is the only case worth closing for.
    const onResize = () => {
      if (window.innerWidth !== openWidth.current) setOpen(false);
      else reposition();
    };

    // pointerdown, not mousedown: iOS only synthesizes mouse events for elements
    // it considers clickable, so tapping blank page area never dismissed these.
    document.addEventListener("pointerdown", onDocDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf.current);
      document.removeEventListener("pointerdown", onDocDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, compute]);

  return {
    open,
    setOpen,
    close,
    coords,
    anchorRef,
    popRef,
    openMenu,
    guardSelect,
    // Focusing the search box on touch summons the keyboard over the panel.
    autoFocus: isFine,
  };
}
